import { isLikelyUserId } from '@/utils/userDisplay';
import { formatEmployeeRecord } from '@/utils/userDisplay';

export type ProjectLabelSource = {
  id: string;
  project_number?: string | null;
  project_name?: string | null;
};

export type PmvProfileLabelSource = {
  id: string;
  asset_code?: string | null;
  asset_name?: string | null;
  fleet_number?: string | null;
};

export function formatProjectOption(p: ProjectLabelSource): string {
  const number = String(p.project_number ?? '').trim();
  const name = String(p.project_name ?? '').trim();
  if (number && name) return `${number} — ${name}`;
  if (name) return name;
  if (number) return number;
  return 'Untitled project';
}

export function buildProjectIdToLabelMap(projects: ProjectLabelSource[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const p of projects) {
    m.set(p.id, formatProjectOption(p));
  }
  return m;
}

export function resolveProjectDisplayFromMap(
  map: Map<string, string>,
  id: string | null | undefined,
  emptyLabel = '—'
): string {
  if (id == null || id === '') return emptyLabel;
  const trimmed = String(id).trim();
  const hit = map.get(trimmed);
  if (hit) return hit;
  if (isLikelyUserId(trimmed)) return 'Unknown project';
  return trimmed;
}

export function formatPmvProfileOption(p: PmvProfileLabelSource): string {
  const code = String(p.asset_code ?? p.fleet_number ?? '').trim();
  const name = String(p.asset_name ?? '').trim();
  if (code && name) return `${code} — ${name}`;
  if (code) return code;
  if (name) return name;
  return 'PMV unit';
}

export function buildPmvProfileIdToLabelMap(profiles: PmvProfileLabelSource[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const p of profiles) {
    m.set(p.id, formatPmvProfileOption(p));
  }
  return m;
}

export function resolvePmvProfileDisplayFromMap(
  map: Map<string, string>,
  id: string | null | undefined,
  emptyLabel = '—'
): string {
  if (id == null || id === '') return emptyLabel;
  const trimmed = String(id).trim();
  const hit = map.get(trimmed);
  if (hit) return hit;
  if (isLikelyUserId(trimmed)) return 'Unknown unit';
  return trimmed;
}

export function formatEmployeeOption(e: {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  employee_code?: string | null;
}): string {
  const name = formatEmployeeRecord(e);
  const code = String(e.employee_code ?? '').trim();
  if (name && code) return `${name} (${code})`;
  return name || code || 'Employee';
}
